import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import type { EChartsCoreOption } from 'echarts/core';

import { AdminAnalyticsService } from '../../../core/admin/admin-analytics.service';
import { AdminAnalytics } from '../../../core/admin/admin-analytics.types';
import { ThemeService } from '../../../core/theme/theme.service';
import { ChartComponent, chartPalette, axisTheme } from '../../../shared/chart/chart';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { ZarPipe } from '../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format an ISO date string "YYYY-MM-DD" as "DD MMM". */
function fmtDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
}

/** Compute max decided count among handlers for proportional bar width. */
function maxDecided(handlers: { name: string; decided: number }[]): number {
  return handlers.reduce((m, h) => Math.max(m, h.decided), 1);
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-admin-dashboard',
  imports: [
    RouterLink,
    DatePipe,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTableModule,
    ChartComponent,
    IconComponent,
    StatusChipComponent,
    ZarPipe,
    DashPipe,
  ],
  templateUrl: './admin-dashboard.html',
  styleUrl: './admin-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboardComponent implements OnInit {
  private readonly analyticsService = inject(AdminAnalyticsService);
  private readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly analytics = signal<AdminAnalytics | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly windowDays = signal(30);

  // ─── Table column defs ──────────────────────────────────────────────────────
  protected readonly emailColumns = [
    'category_display',
    'subject',
    'to_email',
    'status',
    'created_at',
  ];

  // ─── Chart: Emails by day (bar) ────────────────────────────────────────────
  protected readonly emailsChartOption = computed<EChartsCoreOption>(() => {
    const data = this.analytics();
    const dark = this.themeService.isDark();
    const palette = chartPalette();
    const ax = axisTheme(dark);

    const byDay = data?.emails?.by_day;
    if (!byDay?.days?.length) return this._emptyChart('Notifications sent', dark);

    return {
      color: [palette[5]],
      tooltip: {
        trigger: 'axis',
        backgroundColor: ax.tooltipBg,
        borderColor: ax.split,
        textStyle: { color: ax.tooltipText },
      },
      grid: { left: 0, right: 8, bottom: 0, top: 8, containLabel: true },
      xAxis: {
        type: 'category',
        data: byDay.days.map(fmtDay),
        axisLine: { lineStyle: { color: ax.split } },
        axisLabel: { color: ax.axis, fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: ax.split } },
        axisLabel: { color: ax.axis, fontSize: 10 },
        axisLine: { show: false },
        axisTick: { show: false },
        minInterval: 1,
      },
      series: [
        {
          name: 'Emails',
          type: 'bar',
          data: byDay.counts,
          barMaxWidth: 28,
          itemStyle: { borderRadius: [3, 3, 0, 0] },
        },
      ],
    };
  });

  // ─── Derived data ──────────────────────────────────────────────────────────
  protected readonly maxHandlerDecided = computed(() => {
    const data = this.analytics();
    return data?.handlers ? maxDecided(data.handlers) : 1;
  });

  protected readonly recentEmails = computed(() => {
    return this.analytics()?.emails?.recent?.slice(0, 8) ?? [];
  });

  // ─── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadAnalytics();
  }

  protected retry(): void {
    this.loadAnalytics();
  }

  private loadAnalytics(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.analyticsService
      .get(this.windowDays())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.analytics.set(data);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────
  private _emptyChart(title: string, dark: boolean): EChartsCoreOption {
    const ax = axisTheme(dark);
    return {
      title: {
        text: 'No data yet',
        left: 'center',
        top: 'center',
        textStyle: { color: ax.axis, fontSize: 13, fontWeight: 'normal' },
      },
      series: [],
    };
  }

  protected handlerBarWidth(decided: number): string {
    const max = this.maxHandlerDecided();
    return `${Math.round((decided / max) * 100)}%`;
  }

  /** Smooth-scroll to an on-page section (the Emails card has no dedicated route). */
  protected scrollTo(id: string): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
